import React from "react";
import { SHOW_PHOTOS, LANDSCAPE_PHOTOS } from "@/lib/photos";

/**
 * PhotoWatermark
 * A very-low-opacity duotone bronze wash of a single photo used behind
 * hero blocks and section headings. Feels like the theatre is quietly
 * present behind the type without competing with it.
 */
export function PhotoWatermark({
  index = 0,
  position = "right",
  opacity = 0.18,
  blur = 0,
  className = "",
}) {
  const photo = SHOW_PHOTOS[index % SHOW_PHOTOS.length];
  const positions = {
    right:
      "absolute top-0 right-0 h-full w-[55%] md:w-[45%] [mask-image:linear-gradient(to_left,black,transparent)]",
    left: "absolute top-0 left-0 h-full w-[55%] md:w-[45%] [mask-image:linear-gradient(to_right,black,transparent)]",
    top: "absolute top-0 left-0 w-full h-[45%] [mask-image:linear-gradient(to_bottom,black,transparent)]",
    center:
      "absolute inset-0 [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_75%)]",
    "bottom-right":
      "absolute bottom-0 right-0 h-[65%] w-[55%] [mask-image:radial-gradient(ellipse_at_bottom_right,black,transparent_70%)]",
    "bottom-left":
      "absolute bottom-0 left-0 h-[65%] w-[55%] [mask-image:radial-gradient(ellipse_at_bottom_left,black,transparent_70%)]",
  };
  return (
    <div
      aria-hidden
      className={`pointer-events-none overflow-hidden ${positions[position] || positions.right} ${className}`}
    >
      <img
        src={photo.src}
        alt=""
        loading="lazy"
        className="w-full h-full object-cover"
        style={{
          opacity,
          filter: `sepia(60%) hue-rotate(-15deg) saturate(85%) brightness(0.75) contrast(1.05) ${blur ? `blur(${blur}px)` : ""}`,
          mixBlendMode: "luminosity",
        }}
      />
      {/* Bronze color-wash layer */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, rgba(122,67,29,0.35) 0%, rgba(55,44,46,0.6) 60%, rgba(33,26,28,0.85) 100%)",
        }}
      />
    </div>
  );
}

/**
 * PhotoFilmstrip
 * An auto-scrolling horizontal ribbon of past-show photos. Sits between
 * sections as a visual pulse of the theatre's personality. Duotone by
 * default; can be set to `tone="color"` for full color.
 */
export function PhotoFilmstrip({
  tone = "duotone",
  height = "h-40 md:h-52",
  speed = 60,
  photos,
}) {
  const items = photos || LANDSCAPE_PHOTOS;
  const looped = [...items, ...items];
  const filter =
    tone === "duotone"
      ? "sepia(35%) hue-rotate(-10deg) saturate(90%) brightness(0.85) contrast(1.05)"
      : "none";
  return (
    <div
      className={`relative w-full overflow-hidden border-y border-[#DE9E48]/15 bg-[#211a1c] ${height}`}
      data-testid="photo-filmstrip"
    >
      {/* Left/right fade */}
      <div
        aria-hidden
        className="absolute inset-y-0 left-0 w-24 md:w-40 z-10 pointer-events-none bg-gradient-to-r from-[#211a1c] to-transparent"
      />
      <div
        aria-hidden
        className="absolute inset-y-0 right-0 w-24 md:w-40 z-10 pointer-events-none bg-gradient-to-l from-[#211a1c] to-transparent"
      />
      <div
        className="flex items-stretch gap-3 md:gap-4 h-full animate-filmstrip"
        style={{ animationDuration: `${speed}s` }}
      >
        {looped.map((p, i) => (
          <figure
            key={i}
            className="relative flex-shrink-0 h-full aspect-[16/10] overflow-hidden group"
          >
            <img
              src={p.src}
              alt={p.alt}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              style={{ filter }}
            />
            {/* Bronze color-wash */}
            <div
              aria-hidden
              className="absolute inset-0 opacity-70 group-hover:opacity-40 transition-opacity duration-500"
              style={{
                background:
                  "linear-gradient(180deg, rgba(122,67,29,0.28) 0%, rgba(33,26,28,0.65) 100%)",
              }}
            />
          </figure>
        ))}
      </div>
    </div>
  );
}

/**
 * PhotoMosaic
 * A staggered editorial grid of full-color photographs with a small
 * bronze corner accent on each frame. Used on the homepage and About
 * page to showcase past productions with personality.
 */
export function PhotoMosaic({ photos, className = "" }) {
  const items = photos || SHOW_PHOTOS.slice(0, 6);
  return (
    <div
      className={`grid grid-cols-6 auto-rows-[9rem] md:auto-rows-[11rem] gap-3 md:gap-4 ${className}`}
      data-testid="photo-mosaic"
    >
      {items.map((p, i) => {
        // Editorial layout weights
        const spans = [
          "col-span-4 row-span-2",
          "col-span-2 row-span-1",
          "col-span-2 row-span-1",
          "col-span-3 row-span-2",
          "col-span-3 row-span-1",
          "col-span-6 md:col-span-3 row-span-1",
        ];
        return (
          <figure
            key={i}
            className={`relative overflow-hidden border border-white/8 hover:border-[#DE9E48]/40 group transition-all duration-500 ${spans[i % spans.length]}`}
          >
            {/* Bronze corner marks */}
            <span
              aria-hidden
              className="pointer-events-none absolute top-0 left-0 h-[2px] w-8 bg-[#DE9E48] z-10 opacity-0 group-hover:opacity-100 group-hover:w-16 transition-all duration-500"
            />
            <img
              src={p.src}
              alt={p.alt}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
            />
            {/* Subtle bottom gradient for text-legibility depth */}
            <div
              aria-hidden
              className="absolute inset-0 bg-gradient-to-t from-[#211a1c]/45 via-transparent to-transparent opacity-70 group-hover:opacity-40 transition-opacity duration-500"
            />
          </figure>
        );
      })}
    </div>
  );
}

/**
 * PhotoHeroBand
 * A cinematic full-bleed strip using a single photo with the bronze
 * duotone treatment and heavy vignette for hero moments.
 */
export function PhotoHeroBand({ index = 0, height = "h-72 md:h-96", children }) {
  const photo = SHOW_PHOTOS[index % SHOW_PHOTOS.length];
  return (
    <div className={`relative w-full overflow-hidden ${height}`}>
      <img
        src={photo.src}
        alt={photo.alt}
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          filter: "sepia(45%) hue-rotate(-12deg) saturate(85%) brightness(0.7)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(33,26,28,0.55) 0%, rgba(55,44,46,0.55) 50%, rgba(33,26,28,0.95) 100%)",
        }}
      />
      {/* Bronze hairline top & bottom */}
      <div aria-hidden className="absolute top-0 inset-x-0 h-[1px] bg-[#DE9E48]/40" />
      <div aria-hidden className="absolute bottom-0 inset-x-0 h-[1px] bg-[#DE9E48]/40" />
      <div className="relative z-10 w-full h-full flex items-center justify-center px-6">
        {children}
      </div>
    </div>
  );
}
